import { getApiData, postFormData } from "../utils/APIHelper";

const CONTRACTOR_ENDPOINTS = {
  CREATE_PROPOSAL: "api/v2/contractor/proposal",
  LIST_PROPOSALS: "api/v2/contractor/proposal",
  GET_PROPOSAL: (proposalId) => `api/v2/contractor/proposal/${proposalId}`,
  SAVE_BILLING_MILESTONES: (proposalId) =>
    `api/v2/contractor/proposal/${proposalId}/billing-milestones`,
  PROPOSAL_ACTION: (proposalId) =>
    `api/v2/contractor/proposal/${proposalId}/action`,
  CREATE_CUSTOMER: "api/contractor/create-customer",
  SEARCH_CUSTOMERS: "api/contractor/customers",
  PAGINATED_CUSTOMERS: "api/contractor/customers/paginated",
  DELIVERY_APPROVALS: "api/project/delivery-approvals",
  DELIVERY_MEMO: "api/project/delivery-memo",
  DELIVERY_UPLOAD: "api/project/delivery-upload",
  SAVE_PAYMENT_PLAN: (proposalId) =>
    `api/v2/payment-plan/proposal/${proposalId}/view`,
  PROJECT_PRICE: "api/project/price",
  VIEW_PROPOSAL_PAYMENTS: (proposalId) =>
    `api/v2/contractor/proposal/${proposalId}/reno-fees`,
  CALCULATE_PROPOSAL_PAYMENTS: (proposalId) =>
    `api/v2/contractor/proposal/${proposalId}/calculate-payments`,
  SAVE_PROPOSAL_PAYMENTS: (proposalId) =>
    `api/v2/contractor/proposal/${proposalId}/save-payments`,
  PROJECT_BILLING_MILESTONES: (projectId) =>
    `api/projects/${projectId}/billing-milestones`,
  DELIVERY_APPROVAL_BY_ID: (approvalId) =>
    `api/project/delivery-approvals/${approvalId}`,
  DELIVERY_APPROVAL_ACTION: (approvalId) =>
    `api/project/delivery-approvals/${approvalId}/action`,
  DELIVERY_APPROVAL_FILE: (fileId) =>
    `api/project/delivery-approvals/design-files/${fileId}`,
  DELIVERY_MEMO_BY_ID: (memoId) => `api/project/delivery-memo/${memoId}`,
  DELIVERY_MEMO_ACTION: (memoId) =>
    `api/project/delivery-memo/${memoId}/action`,
  CONTRACTOR_BALANCE_SHEET: (contractorId) =>
    `api/v2/contractors/${contractorId}/balanceSheet`,
  CUSTOMER_PAYMENT_LINES: "api/v2/paymentlines/customer",
  CUSTOMER_PAYMENT_SUMMARY: "api/v2/paymentlines/summary/by-projects",
  PROJECT_PAYOUTS: (projectId) => `api/v2/payout/project/${projectId}`,
  HOMEOWNER_PAYMENT_LINES: (proposalId) =>
    `api/v2/paymentlines/homeowner/proposal/${proposalId}`,
  // Contractor proposal change requests
  CHANGE_REQUESTS: (proposalId) =>
    `api/v2/contractor/proposal/${proposalId}/change-requests`,
  CHANGE_REQUEST_BY_ID: (proposalId, crId) =>
    `api/v2/contractor/proposal/${proposalId}/change-requests/${crId}`,
  CHANGE_REQUEST_ACTION: (proposalId, crId) =>
    `api/v2/contractor/proposal/${proposalId}/change-requests/${crId}/action`,
  CHANGE_REQUEST_PREVIEW: (proposalId, crId) =>
    `api/v2/contractor/proposal/${proposalId}/change-requests/${crId}/preview`,
  CHANGE_REQUEST_MILESTONES: (proposalId, crId) =>
    `api/v2/contractor/proposal/${proposalId}/change-requests/${crId}/billing-milestones`,
  CHANGE_REQUEST_MILESTONES_BULK: (proposalId, crId) =>
    `api/v2/contractor/proposal/${proposalId}/change-requests/${crId}/billing-milestones/bulk`,
  CHANGE_REQUEST_MILESTONE_BY_ID: (proposalId, crId, paymentGroupId) =>
    `api/v2/contractor/proposal/${proposalId}/change-requests/${crId}/billing-milestones/${paymentGroupId}`,
  CHANGE_REQUEST_PAYMENT_PLAN: (proposalId, crId) =>
    `api/v2/contractor/proposal/${proposalId}/change-requests/${crId}/payment-plan`,
};

/**
 * Create a contractor proposal (RFC).
 * Payload shape:
 * {
 *   description: string,
 *   scope: string,
 *   timeline: string (JSON),
 *   client_id: number,
 *   project_name: string,
 *   image_url: string,
 *   location: string,
 *   customer_name: string,
 *   customer_email: string
 * }
 */
export const createContractorProposal = async (payload) => {
  try {
    const response = await getApiData(
      CONTRACTOR_ENDPOINTS.CREATE_PROPOSAL,
      "POST",
      payload,
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error("Error creating contractor proposal:", error);
    throw error;
  }
};

/**
 * Update an existing contractor proposal by id.
 * Same payload shape as createContractorProposal.
 */
export const updateContractorProposal = async (proposalId, payload) => {
  try {
    const response = await getApiData(
      CONTRACTOR_ENDPOINTS.GET_PROPOSAL(proposalId),
      "PUT",
      payload,
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error("Error updating contractor proposal:", error);
    throw error;
  }
};

/**
 * List contractor proposals.
 * Optional `status` filters server-side. Pass falsy for "all".
 */
export const listContractorProposals = async ({ status, page, perPage } = {}) => {
  try {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (page) params.append("page", String(page));
    if (perPage) params.append("per_page", String(perPage));
    const qs = params.toString();
    const url = qs
      ? `${CONTRACTOR_ENDPOINTS.LIST_PROPOSALS}?${qs}`
      : CONTRACTOR_ENDPOINTS.LIST_PROPOSALS;
    const response = await getApiData(url, "GET", {}, {}, true);
    return response;
  } catch (error) {
    console.error("Error fetching contractor proposals:", error);
    throw error;
  }
};

/**
 * Create / link a customer to the contractor.
 * Payload shape:
 * {
 *   first_name, last_name, email,
 *   phone_code, phone_no, managed_by
 * }
 */
export const createCustomer = async (payload) => {
  try {
    const response = await getApiData(
      CONTRACTOR_ENDPOINTS.CREATE_CUSTOMER,
      "POST",
      payload
    );
    return response;
  } catch (error) {
    console.error("Error creating customer:", error);
    throw error;
  }
};

/**
 * Search the contractor's customers by free-text query.
 */
export const searchCustomers = async (search = "") => {
  try {
    const url = search
      ? `${CONTRACTOR_ENDPOINTS.SEARCH_CUSTOMERS}?search=${encodeURIComponent(
          search
        )}`
      : CONTRACTOR_ENDPOINTS.SEARCH_CUSTOMERS;
    const response = await getApiData(url, "GET", {});
    return response;
  } catch (error) {
    console.error("Error searching customers:", error);
    throw error;
  }
};

/**
 * Fetch a single contractor proposal by id.
 */
export const getContractorProposal = async (proposalId) => {
  try {
    const response = await getApiData(
      CONTRACTOR_ENDPOINTS.GET_PROPOSAL(proposalId),
      "GET",
      {},
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error("Error fetching contractor proposal:", error);
    throw error;
  }
};

/**
 * Trigger a workflow action on a contractor proposal (e.g. SUBMIT).
 */
export const submitContractorProposalAction = async (proposalId, action) => {
  try {
    const response = await getApiData(
      CONTRACTOR_ENDPOINTS.PROPOSAL_ACTION(proposalId),
      "POST",
      { action },
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error("Error performing proposal action:", error);
    throw error;
  }
};

/**
 * Save billing milestones for a contractor proposal.
 * Sends the milestones as an array of milestone objects; the deliverables
 * field on each milestone is itself a stringified array.
 */
export const saveContractorBillingMilestones = async (
  proposalId,
  milestones,
  method = "POST"
) => {
  try {
    const body =
      typeof milestones === "string" ? JSON.parse(milestones) : milestones || [];
    const httpMethod = String(method).toUpperCase() === "PUT" ? "PUT" : "POST";
    console.log("Billing milestones payload:", body, "method:", httpMethod);
    const response = await getApiData(
      CONTRACTOR_ENDPOINTS.SAVE_BILLING_MILESTONES(proposalId),
      httpMethod,
      body,
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error("Error saving billing milestones:", error);
    throw error;
  }
};

/**
 * Delete a contractor proposal.
 */
export const deleteContractorProposal = async (proposalId) => {
  try {
    const response = await getApiData(
      CONTRACTOR_ENDPOINTS.GET_PROPOSAL(proposalId),
      "DELETE",
      {},
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error("Error deleting contractor proposal:", error);
    throw error;
  }
};

/**
 * Persist the payment plan derived from billing milestones.
 * Backend endpoint: POST /api/v2/payment-plan/proposal/{proposalId}/view
 */
export const savePaymentPlan = async (proposalId, payload) => {
  try {
    const response = await getApiData(
      CONTRACTOR_ENDPOINTS.SAVE_PAYMENT_PLAN(proposalId),
      "POST",
      payload,
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error("Error saving payment plan:", error);
    throw error;
  }
};

/**
 * Upload a single file via the shared delivery-upload endpoint.
 * POST /api/project/delivery-upload  body: FormData { file: <File> }
 *
 * Backend response shape:
 *   { success, message, url, path, mime_type }
 *
 * Returns `{ url, mime_type }` (or null on failure). `mime_type` is
 * surfaced so callers can stash it on the local entry and forward it
 * back to the server on POST/PATCH payloads (design_file_urls /
 * proof_urls entries).
 */
export const uploadDeliveryFile = async (file) => {
  if (!file) return null;
  try {
    const fd = new FormData();
    fd.append("file", file);
    const res = await postFormData(
      CONTRACTOR_ENDPOINTS.DELIVERY_UPLOAD,
      fd,
      {},
      false,
      "POST"
    );
    const url =
      res?.data?.url ||
      res?.data?.image_url ||
      res?.data?.path ||
      res?.url ||
      res?.image_url ||
      res?.path ||
      (typeof res === "string" ? res : null);
    if (!url) return null;
    const mime_type =
      res?.data?.mime_type ||
      res?.mime_type ||
      res?.data?.mimeType ||
      res?.mimeType ||
      file.type ||
      null;
    return { url, mime_type };
  } catch (error) {
    console.error("Error uploading delivery file:", error);
    throw error;
  }
};

/**
 * Read a URL out of an "existing file" object — covers the shapes the
 * approval/memo GET endpoints return ({url}, {image_url}, {path}). Used
 * so files already on the server are passed through as-is instead of
 * being re-uploaded on every save.
 */
const existingFileUrl = (item) => {
  if (!item) return null;
  if (typeof item === "string") return item;
  return item.url || item.image_url || item.path || null;
};

// Pull a mime_type off an existing (already-uploaded) entry. Falls
// through the common field names so any prior shape works.
const existingMimeType = (item) => {
  if (!item) return null;
  return (
    item.mime_type ||
    item.mimeType ||
    item.extension ||
    item.file_extention ||
    null
  );
};

/**
 * Walk a tagged-files map ({ tag: [File|existingFile], ... }) and build
 * the `design_file_urls` array the new approval/memo APIs expect. New
 * Files are uploaded to the shared delivery-upload endpoint first;
 * existing server files (already have a URL) are forwarded unchanged so
 * the backend doesn't get duplicate uploads on save. Each entry carries
 * `mime_type` (from the upload response, falling back to the entry's
 * own field) so the backend can store the real MIME alongside the URL.
 */
const buildDesignFileUrlsFromTagged = async (tagged = {}) => {
  const out = [];
  for (const tag of Object.keys(tagged || {})) {
    const list = Array.isArray(tagged[tag]) ? tagged[tag] : [tagged[tag]];
    for (const item of list) {
      if (!item) continue;
      if (item instanceof File) {
        const uploaded = await uploadDeliveryFile(item);
        if (uploaded?.url) {
          out.push({
            tag,
            url: uploaded.url,
            mime_type: uploaded.mime_type || item.type || null,
          });
        }
      } else {
        const url = existingFileUrl(item);
        if (url) out.push({ tag, url, mime_type: existingMimeType(item) });
      }
    }
  }
  return out;
};

/**
 * Walk a flat list of files (or already-uploaded entries) and build the
 * `proof_urls` array. Same upload-or-passthrough logic as the tagged
 * helper above but without the tag — proofs only carry a URL and a
 * mime_type.
 */
const buildProofUrls = async (proofs = []) => {
  const out = [];
  const seenUrls = new Set();
  for (const item of Array.isArray(proofs) ? proofs : []) {
    if (!item) continue;
    if (item instanceof File) {
      const uploaded = await uploadDeliveryFile(item);
      if (uploaded?.url) {
        const u = String(uploaded.url).trim();
        if (seenUrls.has(u)) continue;
        seenUrls.add(u);
        out.push({
          url: uploaded.url,
          mime_type: uploaded.mime_type || item.type || null,
        });
      }
    } else {
      const url = existingFileUrl(item);
      if (url) {
        const key = String(url).trim();
        if (seenUrls.has(key)) continue;
        seenUrls.add(key);
        out.push({ url, mime_type: existingMimeType(item) });
      }
    }
  }
  return out;
};

/**
 * Resolve a single optional file to a URL — uploads when it's a `File`,
 * passes through when it's an existing server entry. Used for the
 * memo's single `invoice` slot (sent as a bare `invoice_url` string in
 * the payload, so no mime_type goes on the wire here).
 */
const resolveSingleFileUrl = async (file) => {
  if (!file) return null;
  if (file instanceof File) {
    const uploaded = await uploadDeliveryFile(file);
    return uploaded?.url || null;
  }
  return existingFileUrl(file);
};

/**
 * Create a new delivery approval (no id yet).
 * Uploads any pending Files to /delivery-upload first, then POSTs the
 * resulting URL list as JSON.
 *
 * Payload:
 *   {
 *     payment_group_id,
 *     activities: [...],
 *     design_file_urls: [{ tag, url }, ...]
 *   }
 */
export const submitDeliveryApprovals = async (
  paymentGroupId,
  files = {},
  activities = []
) => {
  try {
    const design_file_urls = await buildDesignFileUrlsFromTagged(files);
    const pgIdNum = Number(paymentGroupId);
    const payload = {
      payment_group_id: Number.isFinite(pgIdNum) ? pgIdNum : paymentGroupId,
      activities: Array.isArray(activities) ? activities : [],
      design_file_urls,
    };
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_APPROVALS,
      "POST",
      payload,
      undefined,
      false
    );
  } catch (error) {
    console.error("Error submitting delivery approvals:", error);
    throw error;
  }
};

/**
 * PATCH an existing delivery approval — same upload+JSON flow as create.
 * `payment_group_id` is intentionally omitted on PATCH (backend already
 * has it from the approval row).
 */
export const patchDeliveryApproval = async (
  approvalId,
  files = {},
  activities = []
) => {
  try {
    const design_file_urls = await buildDesignFileUrlsFromTagged(files);
    const payload = {
      activities: Array.isArray(activities) ? activities : [],
      design_file_urls,
    };
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_APPROVAL_BY_ID(approvalId),
      "PATCH",
      payload,
      undefined,
      false
    );
  } catch (error) {
    console.error("Error patching delivery approval:", error);
    throw error;
  }
};

/**
 * Fetch a single delivery approval (with design_files).
 * GET /api/project/delivery-approvals/:id
 */
export const getDeliveryApprovalById = async (approvalId) => {
  try {
    const data = await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_APPROVAL_BY_ID(approvalId),
      "GET",
      {},
      undefined,
      false
    );

    return data.data
  } catch (error) {
    console.error("Error fetching delivery approval:", error);
    throw error;
  }
};

/**
 * Delete an entire delivery approval (only valid for DRAFT status).
 * DELETE /api/project/delivery-approvals/:id
 */
export const deleteDeliveryApproval = async (approvalId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_APPROVAL_BY_ID(approvalId),
      "DELETE",
      {},
      undefined,
      false
    );
  } catch (error) {
    console.error("Error deleting delivery approval:", error);
    throw error;
  }
};

/**
 * Delete a single uploaded file from a delivery approval / memo flow.
 * DELETE /api/project/delivery-approvals/design-files/:fileId
 * Body: `{ tag }` where `tag` is `"proof"` (delivery memo proofs) or
 * `"design_file"` (shop drawings, BOQs, material specs, approval-only files).
 */
export const deleteDeliveryApprovalFile = async (
  fileId,
  tag = "design_file"
) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_APPROVAL_FILE(fileId),
      "DELETE",
      { tag },
      undefined,
      false
    );
  } catch (error) {
    console.error("Error deleting delivery approval file:", error);
    throw error;
  }
};

/**
 * Action endpoint — Submit / Approve / Reject a delivery approval.
 * PUT /api/project/delivery-approvals/:id/action  body: { status }
 */
export const actionDeliveryApproval = async (approvalId, status) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_APPROVAL_ACTION(approvalId),
      "PUT",
      { status },
      undefined,
      false
    );
  } catch (error) {
    console.error("Error actioning delivery approval:", error);
    throw error;
  }
};

/**
 * Project-level billing milestones — includes nested delivery_approvals[]
 * and delivery_memos[] for each milestone (used by ContractorProjectDetails
 * to render the per-milestone Send for Approval / View Approval CTA).
 * GET /api/projects/:projectId/billing-milestones
 */
export const getProjectBillingMilestones = async (projectId) => {
  try {
    const data = await getApiData(
      CONTRACTOR_ENDPOINTS.PROJECT_BILLING_MILESTONES(projectId),
      "GET",
      {},
      undefined,
      false
    );

    return data.data
  } catch (error) {
    console.error("Error fetching project billing milestones:", error);
    throw error;
  }
};

/**
 * Fetch previously-submitted delivery approvals (no id — backend infers context).
 */
export const getDeliveryApprovals = async () => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_APPROVALS,
      "GET",
      {},
      undefined,
      false
    );
  } catch (error) {
    console.error("Error fetching delivery approvals:", error);
    throw error;
  }
};

/**
 * Build the JSON body shared by create / save delivery memo. Uploads
 * any pending Files via /delivery-upload first, then composes the
 * payload the backend expects:
 *   {
 *     payment_group_id?,
 *     activities: [...],
 *     design_file_urls: [{ tag, url }, ...],
 *     proof_urls: [{ url }, ...],
 *     invoice_url: string | null,
 *   }
 *
 * - `files`       — keyed by section tag ("Shop Drawing" / "Boqs" /
 *                   "Material Specs"); File entries are uploaded,
 *                   already-uploaded entries are passed through.
 * - `proofFiles`  — flat array (Files or {url}).
 * - `invoiceFile` — single File or pre-uploaded entry.
 * - `invoiceUrlOverride` — when set (e.g. "" to clear), sent as `invoice_url`
 *   and `invoiceFile` is ignored.
 *
 * `deliveryApprovalId` is accepted for backward-compat with older
 * callers but intentionally NOT included in the new payload (the new
 * backend flow ties memo ↔ approval via payment_group_id alone).
 */
const buildDeliveryMemoPayload = async ({
  paymentGroupId = null,
  files = {},
  activities = [],
  proofFiles = [],
  invoiceFile = null,
  invoiceUrlOverride,
} = {}) => {
  const [design_file_urls, proof_urls] = await Promise.all([
    buildDesignFileUrlsFromTagged(files),
    buildProofUrls(proofFiles),
  ]);
  const invoice_url =
    invoiceUrlOverride !== undefined
      ? invoiceUrlOverride
      : await resolveSingleFileUrl(invoiceFile);
  const payload = {
    activities: Array.isArray(activities) ? activities : [],
    design_file_urls,
    proof_urls,
    invoice_url,
  };
  if (paymentGroupId !== undefined && paymentGroupId !== null) {
    const n = Number(paymentGroupId);
    payload.payment_group_id = Number.isFinite(n) ? n : paymentGroupId;
  }
  return payload;
};

/**
 * Create a new delivery memo (no id yet) — uploads pending files then
 * POSTs the JSON payload.
 * POST /api/project/delivery-memo
 */
export const submitDeliveryMemo = async (
  paymentGroupId,
  files = {},
  activities = [],
  proofFiles = [],
  // Accepted for backward compat with older callers but no longer
  // forwarded to the backend.
  // eslint-disable-next-line no-unused-vars
  _deliveryApprovalId = null,
  invoiceFile = null
) => {
  try {
    const payload = await buildDeliveryMemoPayload({
      paymentGroupId,
      files,
      activities,
      proofFiles,
      invoiceFile,
    });
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_MEMO,
      "POST",
      payload,
      undefined,
      false
    );
  } catch (error) {
    console.error("Error submitting delivery memo:", error);
    throw error;
  }
};

/**
 * PATCH an existing delivery memo — same upload+JSON shape as create.
 * `payment_group_id` is omitted on PATCH (the memo row already has it).
 * PATCH /api/project/delivery-memo/:memoId
 *
 * `options.invoiceUrlOverride` — e.g. "" to clear an existing invoice without
 * calling the design-files delete endpoint.
 */
export const patchDeliveryMemo = async (
  memoId,
  files = {},
  activities = [],
  proofFiles = [],
  // Accepted for backward compat with older callers but no longer
  // forwarded to the backend.
  // eslint-disable-next-line no-unused-vars
  _deliveryApprovalId = null,
  invoiceFile = null,
  options = {}
) => {
  try {
    const { invoiceUrlOverride } = options || {};
    const payload = await buildDeliveryMemoPayload({
      // Skip payment_group_id on PATCH per spec.
      paymentGroupId: null,
      files,
      activities,
      proofFiles,
      invoiceFile,
      invoiceUrlOverride,
    });
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_MEMO_BY_ID(memoId),
      "PATCH",
      payload,
      undefined,
      false
    );
  } catch (error) {
    console.error("Error patching delivery memo:", error);
    throw error;
  }
};

/**
 * Fetch a single delivery memo with its design_files + proof images.
 * GET /api/project/delivery-memo/:memoId
 */
export const getDeliveryMemoById = async (memoId) => {
  try {
    const data = await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_MEMO_BY_ID(memoId),
      "GET",
      {},
      undefined,
      false
    );

    return data.data
  } catch (error) {
    console.error("Error fetching delivery memo:", error);
    throw error;
  }
};

/**
 * Delete a delivery memo entirely (only valid when status === DRAFT).
 * DELETE /api/project/delivery-memo/:memoId
 */
export const deleteDeliveryMemo = async (memoId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_MEMO_BY_ID(memoId),
      "DELETE",
      {},
      undefined,
      false
    );
  } catch (error) {
    console.error("Error deleting delivery memo:", error);
    throw error;
  }
};

/**
 * Action endpoint — Submit / Approve / Reject a delivery memo.
 * PUT /api/project/delivery-memo/:memoId/action  body: { status }
 */
export const actionDeliveryMemo = async (memoId, status) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_MEMO_ACTION(memoId),
      "PUT",
      { status },
      undefined,
      false
    );
  } catch (error) {
    console.error("Error actioning delivery memo:", error);
    throw error;
  }
};

/**
 * Contractor settlements (balance sheet) for a project.
 * GET /api/v2/contractors/:contractorId/balanceSheet?projectId=:projectId
 */
export const getContractorBalanceSheet = async (contractorId, projectId) => {
  try {
    const url = `${CONTRACTOR_ENDPOINTS.CONTRACTOR_BALANCE_SHEET(contractorId)}?projectId=${encodeURIComponent(projectId)}`;
    return await getApiData(url, "GET", {}, undefined, true);
  } catch (error) {
    console.error("Error fetching contractor balance sheet:", error);
    throw error;
  }
};

/**
 * Customer payment lines for a project.
 * GET /api/v2/paymentlines/customer?projectId=:projectId&size=:size
 */
export const getCustomerPaymentLines = async ({ projectId, size = 50 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (projectId != null) params.append("projectId", String(projectId));
    if (size != null) params.append("size", String(size));
    const url = `${CONTRACTOR_ENDPOINTS.CUSTOMER_PAYMENT_LINES}?${params.toString()}`;
    return await getApiData(url, "GET", {}, undefined, true);
  } catch (error) {
    console.error("Error fetching customer payment lines:", error);
    throw error;
  }
};

/**
 * Proposal-stage settlements — contractor payouts derived directly from
 * the proposal's billing milestones (no project-level payment lines yet).
 * GET /api/v2/payout/project/:projectId
 */
export const getProjectPayouts = async (projectId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.PROJECT_PAYOUTS(projectId),
      "GET",
      {},
      undefined,
      true
    );
  } catch (error) {
    console.error("Error fetching project payouts:", error);
    throw error;
  }
};

/**
 * Proposal-stage customer payments — homeowner-facing payment lines
 * generated from the proposal's payment plan (used while stage is still
 * PROPOSAL, before payment lines exist at the project level).
 * GET /api/v2/paymentlines/homeowner/proposal/:proposalId
 */
export const getHomeownerPaymentLines = async (proposalId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.HOMEOWNER_PAYMENT_LINES(proposalId),
      "GET",
      {},
      undefined,
      true
    );
  } catch (error) {
    console.error("Error fetching homeowner payment lines:", error);
    throw error;
  }
};

/**
 * Aggregated payment summary across one or more projects.
 * GET /api/v2/paymentlines/summary/by-projects?projectIds=:ids
 */
export const getCustomerPaymentSummary = async (projectIds) => {
  try {
    const ids = Array.isArray(projectIds) ? projectIds : [projectIds];
    const url = `${CONTRACTOR_ENDPOINTS.CUSTOMER_PAYMENT_SUMMARY}?projectIds=${ids.join(",")}`;
    return await getApiData(url, "GET", {}, undefined, true);
  } catch (error) {
    console.error("Error fetching customer payment summary:", error);
    throw error;
  }
};

/**
 * Fetch previously-submitted delivery memos (no id — backend infers context).
 */
export const getDeliveryMemos = async () => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.DELIVERY_MEMO,
      "GET",
      {},
      undefined,
      false
    );
  } catch (error) {
    console.error("Error fetching delivery memos:", error);
    throw error;
  }
};

/**
 * Persist project pricing (payment plan + reno fees).
 * POST /api/project/price.
 */
export const saveProjectPrice = async (payload) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.PROJECT_PRICE,
      "POST",
      payload,
      undefined,
      false
    );
  } catch (error) {
    console.error("Error saving project price:", error);
    throw error;
  }
};

/**
 * Fetch the proposal's payments view. Returns one of two shapes:
 *  - When reno-fees are NOT yet setup: `{ renoFee: { applicablePlanName,
 *    setupFee, renoFee, proposalCost, ... } }` — the tier defaults.
 *  - When reno-fees ARE setup: the full configured object with `strategy`,
 *    `feeItems`, `paymentPlan` and `contractorPayouts`.
 * GET /api/v2/contractor/proposal/{proposalId}/reno-fees
 */
export const getProposalViewPayments = async (proposalId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.VIEW_PROPOSAL_PAYMENTS(proposalId),
      "GET",
      {},
      undefined,
      true
    );
  } catch (error) {
    console.error("Error fetching proposal view-payments:", error);
    throw error;
  }
};

/**
 * Compute the customer payment plan + contractor settlements for a given
 * reno-fee strategy. Returns { paymentPlan, contractorPayouts, ... }.
 * POST /api/v2/contractor/proposal/{proposalId}/calculate-payments
 */
export const calculateProposalPayments = async (proposalId, payload) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CALCULATE_PROPOSAL_PAYMENTS(proposalId),
      "POST",
      payload,
      undefined,
      true
    );
  } catch (error) {
    console.error("Error calculating proposal payments:", error);
    throw error;
  }
};

/**
 * Persist the calculated payment plan for the proposal. Same body as
 * calculateProposalPayments.
 * POST /api/v2/contractor/proposal/{proposalId}/save-payments
 */
export const saveProposalPayments = async (proposalId, payload) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.SAVE_PROPOSAL_PAYMENTS(proposalId),
      "POST",
      payload,
      undefined,
      true
    );
  } catch (error) {
    console.error("Error saving proposal payments:", error);
    throw error;
  }
};

/**
 * Paginated list of customers.
 * Query: search, page, per_page
 */
export const getPaginatedCustomers = async ({
  search = "",
  page = 1,
  perPage = 10,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("page", String(page));
    params.append("per_page", String(perPage));
    const qs = params.toString();
    const url = `${CONTRACTOR_ENDPOINTS.PAGINATED_CUSTOMERS}?${qs}`;
    const response = await getApiData(url, "GET", {});
    return response;
  } catch (error) {
    console.error("Error fetching paginated customers:", error);
    throw error;
  }
};

// ─── Contractor Proposal Change Requests ──────────────────────────────
// All CR endpoints live on the core API (Setting.newBaseUrl →
// https://reno-core-api-test.azurewebsites.net/), so every call below
// passes isAlternateUrl=true as the 5th arg to getApiData.

/**
 * List all CRs of type CONTRACTOR_PROPOSAL for the given proposal.
 * GET {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests
 */
export const listProposalChangeRequests = async (proposalId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUESTS(proposalId),
      "GET",
      {},
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error listing change requests:", error);
    throw error;
  }
};

/**
 * Create a DRAFT change request.
 * POST {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests
 * Body: { notes, changeRequestPrice }
 */
export const createProposalChangeRequest = async (proposalId, payload) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUESTS(proposalId),
      "POST",
      payload,
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error creating change request:", error);
    throw error;
  }
};

/**
 * Fetch a single CR by id (full row, including the snapshot JSON).
 * GET {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests/{id}
 */
export const getProposalChangeRequest = async (proposalId, crId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUEST_BY_ID(proposalId, crId),
      "GET",
      {},
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error fetching change request:", error);
    throw error;
  }
};

/**
 * Preview the impact of a CR — what would change if approved.
 * GET {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests/{id}/preview
 */
export const previewProposalChangeRequest = async (proposalId, crId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUEST_PREVIEW(proposalId, crId),
      "GET",
      {},
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error fetching change request preview:", error);
    throw error;
  }
};

/**
 * Advance the CR through its state machine.
 * PUT {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests/{id}/action
 * Body: { action: "SUBMIT" | "APPROVE" | "REJECT" | "CANCEL" }
 */
export const actionProposalChangeRequest = async (proposalId, crId, action) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUEST_ACTION(proposalId, crId),
      "PUT",
      { action },
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error actioning change request:", error);
    throw error;
  }
};

/**
 * Upsert a billing-milestone change in the CR's snapshot.
 * POST {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests/{id}/billing-milestones
 * Body: ProposalBillingMilestoneChangeDto
 */
export const upsertChangeRequestMilestone = async (
  proposalId,
  crId,
  payload,
) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUEST_MILESTONES(proposalId, crId),
      "POST",
      payload,
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error upserting change request milestone:", error);
    throw error;
  }
};

/**
 * Upsert multiple billing-milestone changes in one request.
 * POST {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests/{id}/billing-milestones/bulk
 * Body: Array<ProposalBillingMilestoneChangeDto>
 */
export const bulkUpsertChangeRequestMilestones = async (
  proposalId,
  crId,
  payloadArray,
) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUEST_MILESTONES_BULK(proposalId, crId),
      "POST",
      payloadArray,
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error bulk-upserting change request milestones:", error);
    throw error;
  }
};

/**
 * Remove a previously-saved billing-milestone change from the snapshot.
 * DELETE {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests/{id}/billing-milestones/{paymentGroupId}
 */
export const deleteChangeRequestMilestone = async (
  proposalId,
  crId,
  paymentGroupId,
) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUEST_MILESTONE_BY_ID(
        proposalId,
        crId,
        paymentGroupId,
      ),
      "DELETE",
      undefined,
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error deleting change request milestone:", error);
    throw error;
  }
};

/**
 * Save (replace) the proposed payment plan for a change request.
 * POST {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests/{id}/payment-plan
 * Body: { strategy, paymentLines[] }
 */
export const saveChangeRequestPaymentPlan = async (
  proposalId,
  crId,
  payload,
) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUEST_PAYMENT_PLAN(proposalId, crId),
      "POST",
      payload,
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error saving change request payment plan:", error);
    throw error;
  }
};

/**
 * Fetch the PaymentPlan linked to a change request.
 * GET {newBaseUrl}/api/v2/contractor/proposal/{proposalId}/change-requests/{id}/payment-plan
 * Returns 404 if none saved yet.
 */
export const getChangeRequestPaymentPlan = async (proposalId, crId) => {
  try {
    return await getApiData(
      CONTRACTOR_ENDPOINTS.CHANGE_REQUEST_PAYMENT_PLAN(proposalId, crId),
      "GET",
      undefined,
      undefined,
      true,
    );
  } catch (error) {
    console.error("Error fetching change request payment plan:", error);
    throw error;
  }
};
